'use client';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { colors } from '@startengine/design-system.theme.main';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/legacy/image';
import { useParams, useSearchParams, usePathname } from 'next/navigation';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  ClosedButton,
  InvestBox,
  InvestButton,
  InvestWrapper,
  LoyaltyInner,
  LoyaltySubtitle,
  LoyaltyTitle,
  LoyaltyWrapper,
  TestingButton,
  TextWrapper,
  IraOnlySubtext,
  IraDisclaimer,
} from './styles';
import { getCTAText } from './helpers';
import {
  investNowClick,
  trackEvent,
  trackSEEvent,
  appendPostHogDistinctId,
  ADD_TO_CART_SURFACES,
} from 'utils/analytics';
import { userMenu } from 'utils/links';

import AllRewardsModal from 'components/Offering/Rewards/AllRewardsModal';
import useGetCampaignPageCMSData from 'hooks/useGetCampaignPageCMSData';
import useIsMobile from 'hooks/useIsMobile';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useLoginModal from 'hooks/useLoginModal';
import useOfferingDaysLeft from 'hooks/useOfferingDaysLeft';
import { getAudienceRewards, getOffering, getRewards } from 'services/offerings';
import { LEGACY_RESERVATION_LIST } from 'utils/constants/strings';
import {
  formatCurrencyFlexible,
  getEnv,
  hasUserSeIraAccount,
  imagePath,
  isRetirementFundsOnly,
} from 'utils/helpers';
import { INVESTOR_SIGNALS_BEHAVIORAL_EVENTS } from 'utils/investorSignalsBehavioralProperties';
import { isOverAmountRaised } from 'utils/offerings/helpers';
import useCanadianFeatureFlag from 'hooks/useCanadianFeatureFlag';

const InvestComponent = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const BONUS_TEXT = 'Venture Club';

  // Canadian investor flow controlled by PostHog feature flag 'canadian-investor-flow'
  // When disabled (default): Canadian QPs can invest in US-based offerings at /private
  // When enabled: Redirects to /private-canada/invest for Canadian accreditation flow
  const { isEnabled: isCanadianFeatureEnabled } = useCanadianFeatureFlag();
  const isCanadianRoute = isCanadianFeatureEnabled && pathname?.startsWith('/private-canada/');

  const { data: offeringData } = useQuery({
    queryKey: ['offeringData'],
    queryFn: () => getOffering({ slug: params?.slug?.[0] }),
    staleTime: 15 * 60 * 1000, // cache for 15 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to ensure fresh offering data
  });

  const [getTillOfferingClose] = useOfferingDaysLeft();
  const isMobile = useIsMobile();
  const user = useSelector((state) => state.user);
  const userAccounts = useSelector((state) => state.user.userAccounts);
  const investmentAccountStatus = useSelector((state) => state.user.investmentAccountStatus);
  const isLoadingUser = useSelector((state) => state.user.loading);
  const primaryUserId = useSelector((state) => state.user.primaryUserId);
  const {
    needToLogin,
    verifiedPrimaryUserId,
    loading: partnerLoading,
  } = useSelector((state) => state.partnerVerification);

  const signedIn = useIsSignedIn();

  const [renderLoginModal, setLoginModalOpen] = useLoginModal();

  const [url, setUrl] = useState('');
  const endDays = getTillOfferingClose(offeringData);
  const isOverRaised = isOverAmountRaised(offeringData) || offeringData?.lockedOnWaitlist;
  const slug = offeringData?.slug || params?.slug?.[0];
  const isTTW = offeringData && offeringData.status === 'testing';
  const partner = searchParams?.get('partner');
  const searchParamConnectRequestId = searchParams?.get('connectRequestId');
  const connectRequestId = searchParamConnectRequestId?.replaceAll('/', '');

  const { hideTerms } = useGetCampaignPageCMSData({
    slug,
  });

  const showTerms = !!hideTerms || !isTTW;

  const showInvestButton =
    offeringData &&
    (offeringData.status === 'approved' ||
      offeringData.status === 'pending' ||
      offeringData.status === 'not_submitted');

  const getRerouteUrl = useCallback(
    (isReserved) => {
      // Check for frozen account first - redirect to portfolio instead of investment webapp
      if (investmentAccountStatus?.investmentAccountFrozen) {
        return userMenu.portfolio;
      }

      let url = '';

      if (LEGACY_RESERVATION_LIST.includes(offeringData?.slug)) {
        url = `${process.env.NEXT_PUBLIC_ROOT_URL}/${slug}/reserve`;
      } else if (isReserved) {
        url = `${getEnv()}investment.startengine.com/${slug}/reserve`;
      } else if (window?.location?.hash === '#enforceInvestmentAccount') {
        url = `${getEnv()}investment.startengine.com/${slug}?action=enforceInvestmentAccount`;
      } else {
        url = `${`${getEnv()}investment`}.startengine.com/${slug}`;
      }

      if (partner && connectRequestId) {
        url = `${url}${url.includes('?') ? '&' : '?'}partner=${partner}&connectRequestId=${connectRequestId}`;
      }

      return url;
    },
    [
      offeringData?.slug,
      slug,
      partner,
      connectRequestId,
      investmentAccountStatus?.investmentAccountFrozen,
    ],
  );

  const handleClick = useCallback(
    async (isReserved) => {
      // Check for Canadian investor flow - redirect to Canadian invest page
      if (isCanadianRoute) {
        window.location.href = `/private-canada/invest?offering=${slug}`;
        return;
      }

      if (investmentAccountStatus?.investmentAccountFrozen) {
        window.location.href = userMenu.portfolio;
        return;
      }

      const url = getRerouteUrl(isReserved);

      if (offeringData) {
        investNowClick({
          offeringData,
          user,
          surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE,
        });

        await trackSEEvent(
          INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow,
          offeringData?.slug,
          user,
        );
        await trackEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData);
      }

      const isAuthenticated = !!user?.signedIn;

      trackEvent('invest_auth_redirect_initiated', {
        source: 'invest_now_button',
        offeringSlug: offeringData?.slug,
        isAuthenticated,
        redirectUrl: url,
      });

      if (!isAuthenticated || (needToLogin && !partnerLoading)) {
        setUrl(url);
        setLoginModalOpen(true);
        return;
      }

      if (investmentAccountStatus?.investmentAccountFrozen) {
        window.location.href = userMenu.portfolio;
        return;
      }

      const fullUrl = `${url}${window.location.search ? `${window.location.search}` : ''}`;
      window.location.href = appendPostHogDistinctId(fullUrl);
    },
    [
      offeringData,
      user,
      setLoginModalOpen,
      needToLogin,
      partnerLoading,
      getRerouteUrl,
      investmentAccountStatus,
      slug,
      isCanadianRoute,
    ],
  );

  const onCTAOnClick = useCallback(async () => {
    if (offeringData && offeringData.status === 'not_submitted') {
      return null;
    }

    // Check for frozen account first - redirect directly to portfolio
    if (investmentAccountStatus?.investmentAccountFrozen) {
      window.location.href = userMenu.portfolio;
      return null;
    }

    await handleClick(false);

    return null;
  }, [offeringData, handleClick, investmentAccountStatus?.investmentAccountFrozen]);

  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);
  const convertibleNote = offeringData?.offeringType === 'convertible_note';
  const simpleAgreementForFutureEquity = offeringData?.offeringType === 'safe';
  const CTAText = useMemo(() => getCTAText(offeringData), [offeringData]);

  const renderPricePerShare = useCallback(() => {
    if (simpleAgreementForFutureEquity) {
      return (
        <>
          <strong>{offeringData?.discountRate}</strong>{' '}
          <span
            style={{
              fontWeight: 500,
            }}
          >
            Discount
          </span>
          <br />
        </>
      );
    }

    if (!offeringData?.pricePerShare || !showTerms) {
      return null;
    }

    return (
      <>
        <strong>{formatCurrencyFlexible(Number.parseFloat(offeringData?.pricePerShare))}</strong>
        {isMobile ? <br /> : ' '}
        <span
          style={{
            fontWeight: 500,
          }}
        >
          per share
        </span>
        <br />
      </>
    );
  }, [offeringData, showTerms, simpleAgreementForFutureEquity, isMobile]);

  const renderInterestRate = useCallback(() => {
    return (
      <>
        <strong>
          {offeringData?.debtInterestRate &&
            `${Number.parseFloat(offeringData?.debtInterestRate).toFixed(1)}%`}
        </strong>{' '}
        Interest Rate
        <br />
      </>
    );
  }, [offeringData]);

  const renderInvestBox = useCallback(() => {
    const numberFormat = {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    };

    let pricePerShare = '$0';
    if (offeringData?.pricePerShare) {
      try {
        const formatter = new Intl.NumberFormat(undefined, numberFormat);
        pricePerShare = formatter.format(Number.parseFloat(offeringData?.pricePerShare));
      } catch {
        // Intl.NumberFormat failed, using fallback
        // Fallback formatting
        const amount = Number.parseFloat(offeringData?.pricePerShare);
        if (!Number.isNaN(amount)) {
          pricePerShare = `$${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        }
      }
    }

    return <InvestBox>{pricePerShare} Per Share</InvestBox>;
  }, [offeringData]);

  const { data: audienceRewards } = useQuery({
    queryKey: ['audienceRewards', offeringData?.id, primaryUserId],
    queryFn: () => getAudienceRewards(offeringData?.id, primaryUserId),
    refetchOnMount: true, // Refetch on component mount for fresh rewards data
    refetchOnWindowFocus: true, // Refetch when user returns to page
    staleTime: 2 * 60 * 1000, // 2 minutes - more frequent updates for rewards
    enabled: Boolean(signedIn && offeringData?.id && primaryUserId),
  });

  const { data: rewardData } = useQuery({
    queryKey: ['rewards'],
    queryFn: () =>
      getRewards({
        id: offeringData?.id,
      }),
    refetchOnMount: true, // Refetch on component mount
    refetchOnWindowFocus: true,
    staleTime: 5 * (60 * 1000),
    enabled: Boolean(offeringData?.id),
  });

  const hasRewards = rewardData?.length > 0;

  // check if user scroll down a bit
  const [scrolling, setScrolling] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolling(window.scrollY > 678);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !isMobile) return () => null;
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        handleScroll();
        rafId = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isMobile, handleScroll]);

  const investButtonStyle = useMemo(
    () => ({
      display: 'flex',
      width: '100%',
      justifyContent: 'space-around',
    }),
    [],
  );

  const [isTablet, setIsTablet] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      return width >= 600 && width <= 1200;
    }
    return false;
  });

  useEffect(() => {
    let rafId = null;
    const handleResize = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const width = window.innerWidth;
        setIsTablet(width >= 600 && width <= 1200);
        rafId = null;
      });
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  if (isTablet) {
    return null;
  }

  if (isMobile && offeringData?.status === 'closed') {
    return null;
  }

  // Show skeleton loader while userAccounts is loading for signed-in users
  if (signedIn && isLoadingUser && userAccounts === null) {
    return (
      <InvestWrapper data-scrolling={scrolling ? 'true' : 'false'}>
        <div style={isMobile ? investButtonStyle : { width: '100%' }}>
          {isMobile ? (
            <InvestBox data-cy="timer-reward-box-skeleton" hasrewards={false}>
              <Skeleton variant="text" width="80%" height={24} />
              <Skeleton variant="text" width="60%" height={20} sx={{ mt: 1 }} />
            </InvestBox>
          ) : null}

          {/* Skeleton for loyalty wrapper */}
          {!isMobile && (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={60}
              sx={{ borderRadius: '8px', mb: 1 }}
              data-cy="loyalty-skeleton"
            />
          )}

          {/* Skeleton for invest button */}
          <Grid item>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={isMobile ? 50 : 60}
              sx={{ borderRadius: '4px' }}
              data-cy="invest-button-skeleton"
            />
          </Grid>
        </div>
      </InvestWrapper>
    );
  }

  return (
    <InvestWrapper data-scrolling={scrolling ? 'true' : 'false'}>
      <div style={isMobile ? investButtonStyle : { width: '100%' }}>
        {isMobile ? (
          <InvestBox data-cy="timer-reward-box" hasrewards={hasRewards}>
            <Typography variant="p1Bold">
              {convertibleNote ? renderInterestRate() : renderPricePerShare()}
            </Typography>
            {/* <Typography variant="p1Bold">
            <strong>{offeringData?.sharesAvailable}</strong> Shares Available
          </Typography> */}
            {endDays ? (
              <Typography
                variant="p2Medium"
                sx={{
                  color: '#A81A45',
                }}
              >
                {endDays}
              </Typography>
            ) : (
              offeringData?.status !== 'closed' &&
              hasRewards && (
                <Typography
                  variant="p2Bold"
                  sx={{
                    textDecoration: 'underline',
                    color: colors.SEDark,
                    cursor: 'pointer',
                  }}
                  onClick={handleOpen}
                >
                  Show Rewards
                </Typography>
              )
            )}
            <AllRewardsModal
              open={open}
              handleClose={handleClose}
              sx={{
                top: 0,
                bottom: 0,
                overflowY: 'scroll',
              }}
            />
            {offeringData && offeringData.status === 'closed' ? (
              <Typography
                variant="p2Medium"
                sx={{
                  color: '#A81A45',
                }}
              >
                Offering Closed
              </Typography>
            ) : null}
          </InvestBox>
        ) : null}
        {!isMobile && audienceRewards && audienceRewards?.active ? (
          <LoyaltyWrapper>
            <LoyaltyInner>
              <span>
                <Image
                  src={`${imagePath}/images/offering/loyalty-white.svg`}
                  alt={BONUS_TEXT || 'Owners Bonus Loyalty'}
                  width={26}
                  height={26}
                  color="white"
                />
              </span>
              <TextWrapper>
                <LoyaltyTitle variant="h7">
                  Earn {audienceRewards?.percentBonusShares}%{' '}
                  {convertibleNote ? 'Bonus Interest' : 'Bonus Shares'}
                </LoyaltyTitle>
                <LoyaltySubtitle variant="p3SemiBold">LOYALTY BONUS</LoyaltySubtitle>
              </TextWrapper>
            </LoyaltyInner>
          </LoyaltyWrapper>
        ) : null}
        {showInvestButton ? (
          <Grid
            item
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            sm={{
              direction: 'row',
            }}
            xs={5}
          >
            <Grid item>
              <InvestButton
                disabled={offeringData && offeringData.status === 'not_submitted'}
                color="primary"
                variant="contained"
                onClick={onCTAOnClick}
                data-cy={
                  isMobile
                    ? 'campaign-page-invest-cta-mobile-button'
                    : 'campaign-page-invest-cta-desktop-button'
                }
                className={
                  isMobile
                    ? 'campaign-page-invest-cta-mobile-button'
                    : 'campaign-page-invest-cta-desktop-button'
                }
                sx={{
                  fontSize: isOverRaised && isMobile ? '14px' : '16px',
                  backgroundColor: isOverRaised ? colors?.SEOrange : colors?.SEDark,
                  '&:hover': {
                    zIndex: 100,
                    cursor: 'pointer',
                    backgroundColor: isOverRaised
                      ? `${colors?.SEOrange} !important`
                      : `${colors?.SEDark} !important`,
                  },
                }}
              >
                {isOverRaised ? 'Join the Waitlist' : CTAText}
                {!isMobile && !convertibleNote && !simpleAgreementForFutureEquity && showTerms
                  ? renderInvestBox()
                  : null}
                {!isMobile && simpleAgreementForFutureEquity ? (
                  <InvestBox>
                    {offeringData?.discountRate && `${offeringData?.discountRate}% Discount`}
                  </InvestBox>
                ) : null}
                {!isMobile && convertibleNote ? (
                  <InvestBox>
                    {offeringData?.debtInterestRate &&
                      `${Number.parseFloat(offeringData?.debtInterestRate).toFixed(1)}% Interest Rate`}
                  </InvestBox>
                ) : null}
              </InvestButton>
              {isRetirementFundsOnly(offeringData) && !hasUserSeIraAccount(userAccounts) && (
                <>
                  <IraOnlySubtext color="primary">
                    You can open an IRA with StartEngine
                  </IraOnlySubtext>
                  <IraDisclaimer>
                    Equity Trust Company is the custodian of StartEngine IRAs. StartEngine
                    Crowdfunding, Inc. covers IRA account fees if you invest $20,000 or more per
                    year; otherwise, you are responsible for all applicable fees annually. See{' '}
                    <a
                      href="https://s3.us-east-2.amazonaws.com/forms.equitytrustcompany.info/WealthBridge_Fee-Schedule.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      IRA Fees
                    </a>{' '}
                    for details.
                  </IraDisclaimer>
                </>
              )}
            </Grid>
          </Grid>
        ) : null}
        {offeringData && offeringData.status === 'closed' && (
          <ClosedButton>Offering Closed</ClosedButton>
        )}
        {offeringData && offeringData.status === 'testing' ? (
          <TestingButton onClick={() => handleClick(true)} data-cy="reserve-now-desktop-button">
            Reserve Now
          </TestingButton>
        ) : null}
      </div>
      {renderLoginModal({
        offering: offeringData,
        verifiedPrimaryUserId,
        onClose: (user) => {
          if (user?.event === 'signup') {
            const search = window.location.search ? `${window.location.search}` : '';

            window.location.href = appendPostHogDistinctId(`${url}${search}`);
          }
        },
        onSuccessfulLogin: (response) => {
          if (response?.error) {
            return;
          }

          const pageType =
            offeringData && (offeringData.status === 'test' || offeringData.status === 'testing')
              ? 'reserve page'
              : 'invest page';

          if (window.dataLayer) {
            const watchlistPayload = {
              id: offeringData?.slug,
              name: offeringData?.name,
              customer_id: user?.primaryUserId,
              category: offeringData?.regulationType,
              pageType,
              ecommerce: {
                items: [
                  {
                    item_id: offeringData?.slug,
                    item_name: offeringData?.name,
                    item_category: offeringData?.regulationType,
                    price: offeringData?.pricePerShare
                      ? parseFloat(offeringData.pricePerShare)
                      : undefined,
                    quantity: 1,
                    currency: 'USD',
                  },
                ],
              },
            };
            window.dataLayer.push({ event: 'addToWatchlist', ...watchlistPayload });
            window.dataLayer.push({ event: 'added_to_watchlist', ...watchlistPayload });

            window.dataLayer.push({
              event: 'investNowClick',
              id: offeringData?.slug,
              name: offeringData?.name,
              category: offeringData?.regulationType,
              pageType,
              customer_id: user?.primaryUserId,
              ecommerce: {
                items: [
                  {
                    item_id: offeringData?.slug,
                    item_name: offeringData?.name,
                    item_category: offeringData?.regulationType,
                    price: offeringData?.pricePerShare
                      ? parseFloat(offeringData.pricePerShare)
                      : undefined,
                    quantity: 1,
                    currency: 'USD',
                  },
                ],
              },
            });
          }

          setTimeout(() => {
            const search = window.location.search ? `${window.location.search}` : '';

            window.location.href = appendPostHogDistinctId(`${url}${search}`);
          }, 250);
        },
      })}
    </InvestWrapper>
  );
};

export default InvestComponent;
